import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import SectionExtractedDataEditor from './SectionExtractedDataEditor';
import { safeNormalizeSectionExtractedData } from '../../constants/complianceSectionSchemas';
import { formatChecklistDate } from '../../utils/checklistFormatting';

function StatusDot({ status }) {
  const m = {
    compliant: { cls: 'bg-green-500', label: 'Compliant' },
    needs_attention: { cls: 'bg-amber-500', label: 'Needs Attention' },
    not_applicable: { cls: 'bg-slate-400', label: 'N/A' },
    non_compliant: { cls: 'bg-red-500', label: 'Non-Compliant' },
    partially_compliant: { cls: 'bg-amber-500', label: 'Partially Compliant' },
  };
  const s = m[status] || { cls: 'bg-slate-400', label: status || 'Unknown' };
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold">
      <span className={`w-2 h-2 rounded-full ${s.cls}`} />
      <span className="text-slate-700 dark:text-slate-300">{s.label}</span>
    </span>
  );
}

/**
 * Hover popover showing compliance section extracted data, findings, and sources.
 * sectionKey: single compliance key, or use sectionKeys for multiple sections.
 */
export default function ChecklistSectionPopover({
  sectionKey,
  sectionKeys,
  sectionDetails,
  children,
  onOpenFileAtPage,
}) {
  const keys = sectionKeys?.length ? sectionKeys : (sectionKey ? [sectionKey] : []);
  const details = keys.map((k) => sectionDetails?.[k]).filter(Boolean);
  const [show, setShow] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef(null);
  const popoverRef = useRef(null);
  const anchorRef = useRef(null);

  const updatePopoverPosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const width = Math.min(520, window.innerWidth * 0.9);
    const maxHeight = Math.min(420, window.innerHeight * 0.7);
    const gap = 10;
    const margin = 12;

    let left = rect.right + gap;
    if (left + width > window.innerWidth - margin) {
      left = rect.left - width - gap;
    }
    left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));

    let top = rect.top;
    if (top + maxHeight > window.innerHeight - margin) {
      top = window.innerHeight - maxHeight - margin;
    }
    top = Math.max(margin, top);

    setPopoverPos({ top, left });
  }, []);

  useEffect(() => {
    if (!show) return undefined;
    updatePopoverPosition();
    const onReflow = () => updatePopoverPosition();
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [show, updatePopoverPosition]);

  if (details.length === 0) return <>{children}</>;

  const handleEnter = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      updatePopoverPosition();
      setShow(true);
    }, 150);
  };
  const handleLeave = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setShow(false), 200);
  };

  const popoverContent = show ? (
    <div
      ref={popoverRef}
      style={{ position: 'fixed', top: popoverPos.top, left: popoverPos.left, zIndex: 9999 }}
      className="w-[min(520px,90vw)] max-h-[min(420px,70vh)] overflow-y-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl shadow-2xl p-4 transition-opacity duration-150"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {keys.map((sectionKeyForDetail, di) => {
        const detail = sectionDetails?.[sectionKeyForDetail];
        if (!detail) return null;

        const normalizedExtracted = safeNormalizeSectionExtractedData(
          sectionKeyForDetail,
          { extracted_data: detail.extracted_data || {} },
        );
        const hasExtracted = Object.keys(normalizedExtracted).length > 0;

        return (
          <div key={sectionKeyForDetail} className={di > 0 ? 'mt-4 pt-4 border-t border-slate-200 dark:border-slate-700' : ''}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{detail.label}</span>
              <StatusDot status={detail.status} />
            </div>

            {hasExtracted && (
              <div className="mb-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1.5">Extracted Data</p>
                <SectionExtractedDataEditor
                  sectionKey={sectionKeyForDetail}
                  data={normalizedExtracted}
                  readOnly
                  compact
                  onChange={() => {}}
                />
              </div>
            )}

            {detail.findings && detail.findings.length > 0 && (
              <div className="mb-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1">Findings</p>
                <ul className="space-y-0.5">
                  {detail.findings.map((f, fi) => (
                    <li key={fi} className="flex items-start gap-1.5 text-xs">
                      <span className={`mt-0.5 ${f.passed ? 'text-green-500' : 'text-red-500'}`}>{f.passed ? '✓' : '✗'}</span>
                      <span className={f.passed ? 'text-slate-600 dark:text-slate-400' : 'text-red-600 dark:text-red-400 font-medium'}>{f.description}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {detail.recommendations && detail.recommendations.length > 0 && (
              <div className="mb-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-1">Recommendations</p>
                <ul className="space-y-0.5">
                  {detail.recommendations.map((r, ri) => (
                    <li key={ri} className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-300">
                      <span className="mt-0.5">⚠</span><span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {detail.source && (
              <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg px-3 py-2">
                {detail.source.files?.length > 0 ? (
                  <div className="space-y-1">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1">Source Files</p>
                    {detail.source.files.map((file, fi) => {
                      const fileId = file.student_file_id || file.file_id;
                      const pages = file.pages || [];
                      const pageNums = file.page_numbers || [];
                      return (
                        <div key={fi}>
                          <div className="flex items-center gap-1.5 text-xs">
                            <span>📄</span>
                            {fileId && onOpenFileAtPage ? (
                              <button type="button" onClick={() => onOpenFileAtPage(fileId, null, file.file_name)} className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 underline font-medium">
                                {file.file_name}
                              </button>
                            ) : (
                              <span className="text-slate-600 dark:text-slate-400 font-medium">{file.file_name}</span>
                            )}
                          </div>
                          {(pages.length > 0 || pageNums.length > 0) && (
                            <div className="flex items-center gap-1 mt-0.5 ml-5 flex-wrap">
                              {pages.length > 0 ? pages.map((pg, pi) => (
                                <button
                                  key={pi}
                                  type="button"
                                  onClick={() => onOpenFileAtPage && onOpenFileAtPage(pg.student_file_id || fileId, pg.page, file.file_name)}
                                  className="inline-flex items-center px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-[10px] font-medium hover:bg-indigo-200 dark:hover:bg-indigo-800/40 transition-colors cursor-pointer"
                                  title={`${file.file_name} — p. ${pg.page}`}
                                >
                                  p. {pg.page}
                                </button>
                              )) : pageNums.map((pn, pi) => (
                                <button
                                  key={pi}
                                  type="button"
                                  onClick={() => fileId && onOpenFileAtPage && onOpenFileAtPage(fileId, pn, file.file_name)}
                                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${
                                    fileId && onOpenFileAtPage
                                      ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-200 dark:hover:bg-indigo-800/40 cursor-pointer'
                                      : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                                  }`}
                                  title={`${file.file_name} — p. ${pn}`}
                                  disabled={!fileId || !onOpenFileAtPage}
                                >
                                  p. {pn}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : detail.source.display_text ? (
                  <p className="text-xs text-slate-600 dark:text-slate-400">{detail.source.display_text}</p>
                ) : detail.source.page_numbers?.length > 0 && (
                  <p className="text-xs text-slate-500 dark:text-slate-400">Pages: {detail.source.page_numbers.join(', ')}</p>
                )}
                {detail.source.generated_at && (
                  <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1.5 pt-1.5 border-t border-slate-200 dark:border-slate-700">Generated: {formatChecklistDate(detail.source.generated_at)}</p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  ) : null;

  return (
    <div
      ref={anchorRef}
      className="relative inline-flex items-center"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {children}
      <span className="ml-1.5 text-slate-400 hover:text-indigo-500 cursor-help text-xs select-none" title="View section details">ℹ️</span>
      {popoverContent && createPortal(popoverContent, document.body)}
    </div>
  );
}
